import { ApiError } from "../../api/client";
import type { TeamOperation } from "../../api/proTeams";

export function blockingOperationId(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409 || !error.body || typeof error.body !== "object") return null;
  const id = (error.body as { blocking_operation_id?: unknown }).blocking_operation_id;
  return typeof id === "string" && id.trim() ? id : null;
}

export function parseTeamId(raw: string): number | null {
  if (!/^[1-9]\d*$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value <= 4294967295 ? value : null;
}

export function validSteamId(raw: string): boolean {
  if (!/^\d{17}$/.test(raw)) return false;
  const id = BigInt(raw);
  return id > 76561197960265728n && id <= 76561202255233023n;
}

export function operationLabel(
  op: Pick<TeamOperation, "status" | "invitation_pending">,
): string {
  if (op.status === "unknown") return "Outcome unconfirmed";
  if (op.invitation_pending) return "Awaiting player";
  return (
    (
      {
        queued: "Queued",
        running: "In progress",
        succeeded: "Confirmed",
        failed: "Failed",
      } as Record<string, string>
    )[op.status] ?? op.status
  );
}

export function rosterBlock(operations: TeamOperation[]): string | null {
  if (
    operations.some((op) => op.status === "queued" || op.status === "running")
  )
    return "A team operation is in progress. Wait for its result.";
  if (operations.some((op) => op.status === "unknown" && op.action !== "invite"))
    return "An earlier outcome is unconfirmed. Sync the roster before making more changes.";
  return null;
}

export function mergeOperations(
  history: TeamOperation[],
  tracked?: TeamOperation,
): TeamOperation[] {
  if (!tracked) return history;
  const known = history.find((op) => op.operation_id === tracked.operation_id);
  if (known && Date.parse(known.updated_at) >= Date.parse(tracked.updated_at))
    return history;
  return [
    ...history.filter((op) => op.operation_id !== tracked.operation_id),
    tracked,
  ];
}

export function failureMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409 && error.body && typeof error.body === "object") {
      const detail = (error.body as { message?: unknown }).message;
      if (typeof detail === "string" && detail.trim()) return detail;
    }
    const known: Record<string, string> = {
      team_service_unavailable:
        "Team management is unavailable. Check that the new API is deployed with PostgreSQL and a dedicated team bot.",
      capability_unverified: "This action is not enabled by the API yet.",
      team_state_conflict:
        "The team changed or its bot is busy. Refresh the team and check unresolved operations before trying again.",
      bot_reserved_for_teams: "This bot is reserved for Dota team management.",
      organizer_required: "An organizer account is required for this action.",
      team_rate_limited:
        "The API rate-limited this request. Try again later.",
      team_resource_not_found:
        "The team or bot was not found. Verify the Dota team ID and configured bot username.",
      invalid_team_request:
        "Check the required fields, team revision, and image limits (PNG/JPEG, 512 KiB, 4 megapixels). Descriptions may contain up to 4000 UTF-8 bytes.",
    };
    if (known[error.message]) return known[error.message]!;
    if (error.status === 404)
      return "The team endpoint is unavailable. Deploy the API team-management feature first.";
  }
  return error instanceof Error
    ? error.message
    : "The request could not be completed.";
}

export function dateLabel(raw?: string): string {
  if (!raw) return "Not observed yet";
  const date = new Date(raw);
  return Number.isNaN(date.valueOf()) ? "Unknown time" : date.toLocaleString();
}
